import { relations, sql } from 'drizzle-orm'
import {
  index,
  integer,
  pgEnum,
  pgTable,
  real,
  text,
  uniqueIndex,
} from 'drizzle-orm/pg-core'
import {
  getBaseFields,
  getBaseIndexes,
  getNullableIdReference,
  getTimestampWithTimezone,
  typedJsonb,
} from '../common/utils'
import { customers } from './customer'
import { discountCodes } from './discountCode'
import { emails } from './email'
import { formSubmissions } from './formSubmission'
import { opportunities } from './opportunity'
import { propertyTypeEnum } from './property'
import { shortUrls } from './shortUrl'
import { tasks } from './task'
import { users } from './user'

export interface OpportunityPrefillData {
  user?: {
    firstName?: string
    lastName?: string
    email?: string
    phoneNumber?: string
  }
  saleType?: 'subscription' | 'single_time'
  source?: string
  notes?: string
  data?: {
    address?: {
      street?: string
      city?: string
      postalCode?: string
    }
    propertyType?: (typeof propertyTypeEnum.enumValues)[number]
  }
}

export interface AiQualityScores {
  accuracy: number
  completeness: number
  tone: number
  actionability: number
}

export interface AiClassification {
  category:
    | 'tarjouspyyntö'
    | 'soittopyyntö'
    | 'reklamaatio'
    | 'tiedustelu'
    | 'ajanvaraus'
    | 'laskutus'
    | 'muu'
    | 'roskaposti'
    // Self-generated email delivery notification (bounce / complaint alert sent
    // from our own no-reply address back into the info@ inbox). Set
    // deterministically without AI — see processAiInbox.
    | 'toimitusongelma'
    // Self-generated informational notification (e.g. "Tarjous hyväksytty",
    // "Uusi käynnin varaus") our own system sends into the info@ inbox. Not a
    // delivery problem and not a customer inquiry — set deterministically
    // without AI, see processAiInbox / classifySystemEmail.
    | 'järjestelmäilmoitus'
  extractedData: {
    name?: string
    email?: string
    phone?: string
    address?: string
    propertyType?: string
    services?: string[]
    preferredDate?: string
    notes?: string
  }
  suggestedActions: Array<{
    type:
      | 'create_opportunity'
      | 'create_task'
      | 'archive'
      | 'respond'
      | 'ask_more_info'
    label: string
    prefillData?: OpportunityPrefillData
  }>
  needsMoreInfo: boolean
  missingFields?: string[]
  // Sales work (no deal made yet) vs customer service — see inbox/leadFilter.ts
  isLead?: boolean
  // 2-3 ways to answer, shown as chips; the instruction feeds the ghostwriter
  replyOptions?: Array<{ label: string; instruction: string }>
  // Sales tags picked from the described vocabulary offered in the prompt;
  // attached to the lead on conversion — see applyServiceTags
  tags?: string[]
}

export const inboxStatusEnum = pgEnum('inbox_status', [
  'new',
  'ai_processing',
  'ready',
  'in_progress',
  'done',
])

export const inboxItems = pgTable(
  'inbox_items',
  {
    ...getBaseFields(),

    // Source — at most one is non-null; neither on a 'visit' item
    emailId: getNullableIdReference(() => emails.id),
    formSubmissionId: getNullableIdReference(() => formSubmissions.id),
    // 'visit' = a technician's huoltosuositus, linked to its lead
    channel: text().notNull(), // 'email' | 'form' | 'visit'

    // Workflow
    status: inboxStatusEnum().notNull().default('new'),
    assignedToUserId: getNullableIdReference(() => users.id),
    respondedAt: getTimestampWithTimezone(),

    // When the automatic "we received your message" acknowledgment was sent.
    // Null = not yet acknowledged; doubles as the idempotency guard so the AI
    // inbox processor never sends a second receipt for the same item.
    acknowledgedAt: getTimestampWithTimezone(),

    // Booking-link SMS sent by the automation when no rep took the lead in
    // time (services/inbox/bookingSms). bookingSmsAt is stamped before the
    // send and is the lock against a rep claiming the item at the same moment.
    bookingSmsAt: getTimestampWithTimezone(),
    bookingSmsStatus: text().$type<'sent' | 'failed'>(),
    // Set in the discount arm of the A/B test only.
    bookingSmsDiscountCodeId: getNullableIdReference(() => discountCodes.id),
    bookingSmsShortUrlId: getNullableIdReference(() => shortUrls.id),

    // Linked entities
    opportunityId: getNullableIdReference(() => opportunities.id),
    customerId: getNullableIdReference(() => customers.id),
    taskId: getNullableIdReference(() => tasks.id),

    // Denormalized display fields
    senderEmail: text(),
    senderName: text(),
    subject: text(),
    preview: text(),
    receivedAt: getTimestampWithTimezone(),

    // Conversation threading
    conversationId: text(),

    // AI output
    aiRetryCount: integer().notNull().default(0),
    aiClassification: typedJsonb<AiClassification>('ai_classification'),
    aiDraftResponse: text(),
    aiDraftSubject: text(),
    aiSummary: text(),
    draftResponse: text(), // Human-edited draft, persisted between sessions
    aiConfidence: real(),
    aiProcessedAt: getTimestampWithTimezone(),
    aiModel: text(),
    aiPromptVersion: text(),

    // Human feedback
    humanAction: text(), // 'accepted' | 'modified' | 'rejected'
    humanEditedResponse: text(),
    humanActionAt: getTimestampWithTimezone(),
    humanActionUserId: getNullableIdReference(() => users.id),

    // AI quality evaluation
    aiQualityScore: real(),
    aiQualityScores: typedJsonb<AiQualityScores>('ai_quality_scores'),
    aiQualityReason: text(),
  },
  (table) => [
    ...getBaseIndexes('inbox_items', table),
    index('inbox_items_status_idx').on(table.status),
    uniqueIndex('inbox_items_email_id_unique_idx')
      .on(table.emailId)
      .where(sql`email_id IS NOT NULL`),
    uniqueIndex('inbox_items_form_submission_id_unique_idx')
      .on(table.formSubmissionId)
      .where(sql`form_submission_id IS NOT NULL`),
    index('inbox_items_conversation_id_idx').on(table.conversationId),
    index('inbox_items_received_at_idx').on(table.receivedAt),
    index('inbox_items_assigned_to_idx').on(table.assignedToUserId),
    // The stats sales-channel classifier looks inbox items up by opportunity
    // on every statistics page load (stats/visitSource.ts).
    index('inbox_items_opportunity_id_idx').on(table.opportunityId),
  ],
)

export const inboxItemsRelations = relations(inboxItems, ({ one }) => ({
  email: one(emails, {
    fields: [inboxItems.emailId],
    references: [emails.id],
  }),
  formSubmission: one(formSubmissions, {
    fields: [inboxItems.formSubmissionId],
    references: [formSubmissions.id],
  }),
  assignedTo: one(users, {
    fields: [inboxItems.assignedToUserId],
    references: [users.id],
    relationName: 'inboxItemAssignedTo',
  }),
  opportunity: one(opportunities, {
    fields: [inboxItems.opportunityId],
    references: [opportunities.id],
  }),
  customer: one(customers, {
    fields: [inboxItems.customerId],
    references: [customers.id],
  }),
  task: one(tasks, {
    fields: [inboxItems.taskId],
    references: [tasks.id],
  }),
  humanActionUser: one(users, {
    fields: [inboxItems.humanActionUserId],
    references: [users.id],
    relationName: 'inboxItemHumanAction',
  }),
  bookingSmsDiscountCode: one(discountCodes, {
    fields: [inboxItems.bookingSmsDiscountCodeId],
    references: [discountCodes.id],
  }),
  bookingSmsShortUrl: one(shortUrls, {
    fields: [inboxItems.bookingSmsShortUrlId],
    references: [shortUrls.id],
  }),
}))
